'use client'

import { useMedia } from '@/hooks'
import { cn } from '@/lib/utils'
import { useStoreNavigation } from '@/store'
import { Typo3Header } from '@/types'
import { Button, Popover } from '@/ui/components'
import { MEDIA } from '@spot'
import { memo, useEffect, useRef, useState } from 'react'

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  data: Typo3Header
}

const BottomNav = ({ data }: Props) => {
  //refs
  const ref = useRef(null)
  const { isNavReduced } = useStoreNavigation()
  const [popoverOpenByIndex, setPopoverOpenByIndex] = useState<
    Record<number, boolean>
  >({})

  useEffect(
    () =>
      useStoreNavigation.subscribe((state, prevState) => {
        if (state.isNavReduced && !prevState.isNavReduced) {
          setPopoverOpenByIndex({})
        }
      }),
    []
  )

  //hooks
  const isTablet = useMedia(MEDIA.tablet, true)

  if (!isTablet) {
    return null
  }

  const buttons = [data?.liveStatusButton, data?.shopButton]

  return (
    <aside
      ref={ref}
      className={cn(
        'gap-2xs p-xs fixed bottom-0 left-0 z-300 grid w-full grid-cols-2 bg-neutral-100 shadow-[0_0_1rem_rgb(0,0,0,.2)] md:hidden',
        'ease-out-cubic translate-y-0 transition-transform duration-700',
        '[body[data-nav-frozen="true"]_&]:translate-y-[calc(100%+2rem)] [body[data-nav-reduced="true"]_&]:translate-y-[calc(100%+2rem)]! [body[data-nav-reduced="true"]_&]:data-[open=true]:translate-y-[calc(100%+12rem)]'
      )}
    >
      {buttons.map((button, buttonIndex) => (
        <div
          className="md:hidden"
          key={`nav-bottom-button-${buttonIndex.toString()}`}
        >
          <Popover
            open={!isNavReduced && (popoverOpenByIndex[buttonIndex] ?? false)}
            onOpenChange={(open) => {
              if (open && isNavReduced) {
                return
              }
              setPopoverOpenByIndex((prev) => ({
                ...prev,
                [buttonIndex]: open
              }))
            }}
            className="w-screen p-0"
            button={<Button className="w-full" {...button} />}
          >
            <div className="flex flex-col">
              {button?.menu?.map((menuItem, index) => (
                <Button
                  key={`nav-button-${buttonIndex.toString()}-${index.toString()}`}
                  variant="secondary"
                  icon={menuItem.icon}
                  iconPosition="left"
                  size="m"
                  href={menuItem.href}
                  target={menuItem.target}
                  className="justify-start"
                >
                  {menuItem.linkText}
                </Button>
              ))}
            </div>
          </Popover>
        </div>
      ))}
    </aside>
  )
}

export default memo(BottomNav)
